/**
 * The permanent home of the setup: a bar, a percentage, and every item as a row that opens
 * the screen completing it.
 *
 * Nothing on this page is ticked by hand. The server judges every row from what the firm
 * actually has — a tariff row, a driver holding a licence, an uploaded logo — and the only
 * thing a person can say here is that an item is not needed, which takes it out of the
 * count and can be undone from the same row. The rows the system refuses a booking without
 * offer no such control, because the server would refuse it.
 *
 * The guide on a new firm's first sign-in is a shortcut through the first rows of this
 * page; this page is what stays. `docs/firm-setup.md` is the whole of it.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useTenantFormats } from '../../shell/formats';
import {
  Banner,
  Button,
  Card,
  EmptyState,
  PageHeader,
  Pill,
  Section,
  StatTile,
  type Tone,
} from '../../ui';
import { setupApi, type SetupItem, type SetupState } from '../api';
import { describe } from '../errors';
import { notifySetupChanged } from './events';
import { grouped, routeFor } from './items';

const TONE: Record<SetupItem['state'], Tone> = {
  Todo: 'neutral',
  InProgress: 'info',
  Done: 'ok',
  NotNeeded: 'neutral',
};

export function SetupPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const formats = useTenantFormats();

  const [state, setState] = useState<SetupState | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  /** The one row being marked, by key, so its buttons wait and the others do not. */
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setState(await setupApi.state());
    } catch (failure) {
      setProblem(describe(failure, t, 'setup.couldNotLoad'));
    }
  }, [t]);

  useEffect(() => {
    // eslint-disable-next-line react/set-state-in-effect -- fetching, not cascading
    void load();
  }, [load]);

  async function mark(item: SetupItem, notNeeded: boolean) {
    setBusy(item.key);
    setProblem(null);

    try {
      // The server answers with the whole list again, which is what keeps this screen
      // honest: the percentage on it is the server's, never one computed here.
      setState(await setupApi.mark(item.key, notNeeded));
      notifySetupChanged();
    } catch (failure) {
      setProblem(describe(failure, t, 'setup.couldNotSave'));
    } finally {
      setBusy(null);
    }
  }

  /**
   * The sentence under a row: what satisfied it, who is on their way, or the reason it was
   * declared unnecessary. Data from the server, words from here.
   */
  function detail(item: SetupItem): string | null {
    const values = { ...item.arguments };

    // The starter tariff and rate begin at the Unix epoch — "always", not a day anybody
    // chose — and "since 1 January 1970" would read as a mistake.
    if (values.date !== undefined) {
      values.date =
        Date.parse(values.date) <= 0 ? t('setup.sinceTheStart') : formats.day(values.date);
    }
    if (values.amount !== undefined) values.amount = formats.amount(Number(values.amount));

    // Where a view came from is a code, and the row says it in words.
    if (values.source !== undefined) values.source = t(`map.home.sourceWord.${values.source}`);

    switch (item.state) {
      case 'Done':
        return t(`setup.item.${item.key}.done`, { ...values, defaultValue: '' }) || null;
      case 'InProgress':
        return t(`setup.item.${item.key}.inProgress`, { ...values, defaultValue: '' }) || null;
      case 'NotNeeded':
        return t(`setup.item.${item.key}.notNeededAs`, { defaultValue: t('setup.state.NotNeeded') });
      default:
        return null;
    }
  }

  const guideLabel =
    state?.guide.stage === 'InProgress' ? t('setup.resumeGuide') : t('setup.startGuide');

  return (
    <>
      <PageHeader
        title={t('nav.settings.setup')}
        description={t('nav.settingsAbout.setup')}
        actions={
          state === null ? undefined : (
            <Button onClick={() => void navigate('/setup')}>{guideLabel}</Button>
          )
        }
      />

      {problem !== null && <Banner tone="danger">{problem}</Banner>}

      {/* Nothing to show and nothing loading: somebody the server refused — a dispatcher
          who typed the address — gets the one sentence above and no controls that would
          be refused the same way. */}
      {state === null ? (
        problem === null && (
          <Card>
            <EmptyState>{t('settings.loading')}</EmptyState>
          </Card>
        )
      ) : (
        <>
          <Card>
            <div className="flex items-center gap-5 pr-5">
              <StatTile
                label={t('setup.progress', { settled: state.settled, total: state.total })}
                value={t('setup.percent', { percent: state.percent })}
                tone={state.percent === 100 ? 'ok' : 'brand'}
              />

              <div className="flex-1 min-w-0">
                {/* The bar is a width, which is layout, not a colour: the fill is the
                    brand token and the track the third surface. */}
                <div
                  className="h-2 rounded-pill bg-surface-3 overflow-hidden"
                  role="progressbar"
                  aria-valuenow={state.percent}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <div
                    className={state.percent === 100 ? 'h-full bg-ok' : 'h-full bg-brand'}
                    style={{ width: `${state.percent}%` }}
                  />
                </div>

                <p className="text-secondary text-ink-3 mt-2 mb-0 max-w-[72ch]">
                  {state.percent === 100 ? t('setup.completeHint') : t('setup.intro')}
                </p>
              </div>
            </div>

            {state.percent === 100 && (
              <Banner tone="ok" footer>
                {t('setup.complete')}
              </Banner>
            )}
          </Card>

          {grouped(state.items).map((entry) => (
            <Section
              key={entry.group}
              title={t(`setup.group.${entry.group}`)}
              description={t(`setup.groupHint.${entry.group}`)}
            >
              {entry.items.map((item, index) => {
                const words = detail(item);
                const working = busy === item.key;

                // Open rows in the first group are loud, because they stop a booking;
                // everywhere else an open row is simply not done yet.
                const tone: Tone =
                  item.state === 'Todo' && entry.group === 'beforeFirstBooking'
                    ? 'warn'
                    : TONE[item.state];

                return (
                  <div
                    key={item.key}
                    className={
                      index === entry.items.length - 1
                        ? 'grid grid-cols-[minmax(0,1fr)_auto] gap-6 items-center px-5 py-3'
                        : 'grid grid-cols-[minmax(0,1fr)_auto] gap-6 items-center px-5 py-3 border-b border-rule'
                    }
                  >
                    <div className="min-w-0">
                      <div className="text-body font-medium">
                        {t(`setup.item.${item.key}.title`, { defaultValue: item.key })}
                      </div>
                      <div className="text-secondary text-ink-3 mt-0.5 max-w-[58ch] leading-[1.4]">
                        {t(`setup.item.${item.key}.why`, { defaultValue: '' })}
                      </div>
                      {words !== null && (
                        <div className="text-secondary text-ink-2 mt-1">{words}</div>
                      )}
                    </div>

                    <div className="flex items-center justify-end gap-2">
                      <Pill tone={tone} soft={item.state === 'NotNeeded'}>
                        {t(`setup.state.${item.state}`)}
                      </Pill>

                      {item.canBeNotNeeded && item.state !== 'Done' && (
                        <Button
                          size="sm"
                          variant="quiet"
                          disabled={working}
                          onClick={() => void mark(item, item.state !== 'NotNeeded')}
                        >
                          {item.state === 'NotNeeded' ? t('setup.needed') : t('setup.notNeeded')}
                        </Button>
                      )}

                      {item.state !== 'NotNeeded' && (
                        <Button size="sm" onClick={() => void navigate(routeFor(item.key))}>
                          {t('setup.open')}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </Section>
          ))}
        </>
      )}
    </>
  );
}
